import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { messagesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function MessageThread() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [otherName, setOtherName] = useState('');
  const bottomRef = useRef(null);
  const intervalRef = useRef(null);

  const backPath = user?.role === 'coach' ? '/coach/messages' : '/messages';

  useEffect(() => {
    loadThread();
    intervalRef.current = setInterval(loadThread, 5000);
    return () => clearInterval(intervalRef.current);
  }, [userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadThread = async () => {
    try {
      const res = await messagesAPI.thread(userId);
      setMessages(res.data || []);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const convos = await messagesAPI.conversations();
        const match = (convos.data || []).find(c => c.user_id === userId);
        if (match) setOtherName(match.name);
        else {
          const listFn = user?.role === 'coach' ? messagesAPI.players : messagesAPI.coaches;
          const list = await listFn();
          const m = (list.data || []).find(u => u.id === userId);
          if (m) setOtherName(m.name);
        }
      } catch {}
    })();
  }, [userId, user?.role]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await messagesAPI.send(userId, text.trim());
      setText('');
      await loadThread();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  const formatStamp = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (isToday) return `Today · ${time}`;
    if (isYesterday) return `Yesterday · ${time}`;
    const sameYear = d.getFullYear() === now.getFullYear();
    const datePart = d.toLocaleDateString([], sameYear
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' });
    return `${datePart} · ${time}`;
  };

  const formatDayHeader = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString([], sameYear
      ? { weekday: 'long', month: 'short', day: 'numeric' }
      : { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="h-screen h-[100dvh] bg-[#F8FAFC] text-[#0A2242] flex flex-col" data-testid="message-thread">
      {/* Header - with safe area for PWA */}
      <header className="flex-shrink-0 bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] safe-top" data-testid="thread-header">
        <div className="flex items-center gap-3 px-4 py-3 max-w-lg mx-auto">
          <button onClick={() => navigate(backPath)} className="text-[#64748B] hover:text-[#0A2242] transition-colors p-1" data-testid="thread-back">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-['Barlow_Condensed'] text-lg font-bold tracking-tight" data-testid="thread-name">
            {otherName || 'Message'}
          </h1>
        </div>
      </header>

      {/* Messages - scrollable area fills remaining space */}
      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-lg mx-auto w-full space-y-2">
        {messages.length === 0 && (
          <div className="text-center py-12 text-[#94A3B8] text-sm">No messages yet. Start the conversation!</div>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.sender_id === user?.id;
          const showAuthor = !isMe && msg.sender_name;
          const prev = messages[i - 1];
          const samePrevSender = prev && prev.sender_id === msg.sender_id;
          // Day divider: insert a row whenever the calendar day changes between consecutive messages
          const showDayDivider = !prev || (
            prev && new Date(prev.created_at).toDateString() !== new Date(msg.created_at).toDateString()
          );
          return (
            <React.Fragment key={msg.id}>
              {showDayDivider && (
                <div
                  className="flex items-center justify-center py-2"
                  data-testid={`day-divider-${i}`}
                >
                  <span className="bg-[#E2E8F0]/70 text-[#64748B] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    {formatDayHeader(msg.created_at)}
                  </span>
                </div>
              )}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i < 20 ? i * 0.02 : 0 }}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                    isMe
                      ? 'bg-[#2563EB] text-white rounded-br-md'
                      : 'bg-white border border-[#E2E8F0] text-[#0A2242] rounded-bl-md shadow-sm'
                  }`}
                  data-testid={`msg-${i}`}
                >
                  {showAuthor && !samePrevSender && (
                    <p className={`text-[9px] font-bold uppercase tracking-widest mb-1 ${
                      msg.sender_role === 'coach' ? 'text-[#FF9100]' : 'text-[#64748B]'
                    }`}>
                      {msg.sender_role === 'coach' ? 'Coach ' : ''}{msg.sender_name}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                  <p
                    className={`text-[9px] mt-1 ${isMe ? 'text-blue-200' : 'text-[#94A3B8]'}`}
                    title={new Date(msg.created_at).toLocaleString()}
                  >
                    {formatStamp(msg.created_at)}
                  </p>
                </div>
              </motion.div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input - sticky bottom with safe area */}
      <div className="flex-shrink-0 bg-white border-t border-[#E2E8F0] px-4 pt-3 pb-5 bottom-nav">
        <div className="max-w-lg mx-auto flex gap-2">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 text-base text-[#0A2242] placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none"
            style={{ fontSize: '16px' }}
            data-testid="message-input"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className={`px-4 rounded-xl transition-all ${
              text.trim() ? 'bg-[#2563EB] text-white' : 'bg-[#E2E8F0] text-[#94A3B8]'
            }`}
            data-testid="send-message-btn"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
